import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { XMarkIcon } from "@heroicons/react/20/solid";
import TiptapEditor from "./components/editor/TiptapEditor";
import api from "./components/login/interceptors";
import { BASE_URL } from "./Constants";

const TemplatePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [savedContent, setSavedContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [currentContent, setCurrentContent] = useState("");
  const [editContent, setEditContent] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const extractPlaceholders = (html) => {
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    const placeholders = [];
    let match;
    
    while ((match = placeholderRegex.exec(html)) !== null) {
      let placeholder = match[1].trim();
      
      if (placeholder && /[A-Za-z]/.test(placeholder) && !placeholders.includes(placeholder)) {
        placeholders.push(placeholder);
      }
    }
    
    return placeholders;
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      setIsLoadingTemplate(true);
      try {
        const userDataStringify = localStorage.getItem("user_data");
        if (!userDataStringify) {
          const defaultContent = "<p>Add your Standardized template content here...</p>";
          setCurrentContent(defaultContent);
          setIsLoadingTemplate(false);
          return;
        }

        const token = localStorage.getItem("jwtToken");
        const response = await api.get(`${BASE_URL}/api/templates/active`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && response.data.template_html) {
          const template = response.data;
          setCurrentContent(template.template_html || "");
          setSavedContent(template.template_html || "");
        } else {
          const defaultContent = "<p>Add your Standardized template content here...</p>";
          setCurrentContent(defaultContent);
        }
      } catch (error) {
        // Don't show error if there's no active template (404 or similar)
        const isNotFound = error.response?.status === 404 || 
                          error.response?.status === 400 ||
                          error.response?.data?.message?.toLowerCase().includes('no active template') ||
                          error.response?.data?.error?.toLowerCase().includes('no active template');
        
        if (!isNotFound) {
          const errorMsg = error.response?.data?.message || 
                           error.response?.data?.error ||
                           error.response?.data?.detail ||
                           error.message ||
                           "Failed to load template.";
          setErrorMessage(errorMsg);
        }
        const defaultContent = "<p>Add your Standardized template content here...</p>";
        setCurrentContent(defaultContent);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    fetchTemplate();
  }, []);

  const handleUpdate = (content) => {
    setEditContent(content);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage("");
    const content = editContent || currentContent;
    
    try {
      const userDataStringify = localStorage.getItem("user_data");
      if (!userDataStringify) {
        throw new Error("User data not found");
      }

      const placeholders = extractPlaceholders(content);

      const templateData = {
        format: "html",
        template_html: content,
        placeholders: placeholders,
      };

      const token = localStorage.getItem("jwtToken");
      // Always use POST for save (upsert operation)
      const response = await api.post(`${BASE_URL}/api/templates/`, templateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data?._id) {
        setTemplateId(response.data._id);
      }

      setSavedContent(content);
      setCurrentContent(content);
      setIsEditing(false);
      setSaveMessage("Template saved successfully!");
      setTimeout(() => {
        setSaveMessage("");
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.error ||
                       error.response?.data?.detail ||
                       error.response?.data?.error_message ||
                       error.message ||
                       "Failed to save template. Please try again.";
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    const contentToEdit = currentContent || savedContent || "<p>Add your Standardized template content here...</p>";
    setEditContent(contentToEdit);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditContent("");
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col h-screen w-full" style={{ backgroundColor: theme.palette.primary.v2 }}>
      {/* Header */}
      <div
        className="flex justify-between items-center px-4 sm:px-6 py-5 border-b max-sm:pl-12"
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.main,
          borderColor: theme.palette.secondary.v4,
        }}
      >
        <div className="flex items-center gap-2">
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Go back"
            sx={{
              color: theme.palette.text.main,
              padding: "0px",
              borderRadius: "4px",
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        <h1 className="text-xl md:text-2xl font-semibold">Standardized resume template</h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {!isEditing ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleEdit}
              sx={{
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
                textTransform: 'none',
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.secondary.v3,
                  color: theme.palette.text.light,
                },
              }}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{
                  backgroundColor: '#fff',
                  color: theme.palette.text.main,
                  border: '1px solid',
                  borderColor: theme.palette.secondary.main,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  '&.Mui-disabled': {
                    backgroundColor: '#fff',
                    color: '#9CA3AF',
                    borderColor: '#D1D5DB',
                    cursor: 'not-allowed',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  backgroundColor: isSaving
                    ? theme.palette.secondary.v3
                    : theme.palette.info.main,
                  color: isSaving ? '#9CA3AF' : theme.palette.text.light,
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.secondary.v3,
                    color: theme.palette.text.light,
                  },
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div 
        className="flex-1 mx-6 max-md:mx-4 overflow-auto hide-scrollbar pb-5 mb-6"
      >
        <div className="max-w-4xl mx-auto flex flex-col">
          {/* Success Banner */}
          {saveMessage && (
            <div
              className="mt-4 mb-4 rounded-lg border p-4 flex items-center justify-between"
              style={{
                backgroundColor: "#F0FDF4",
                borderColor: "#86EFAC",
              }}
            >
              <div className="flex items-center gap-3 flex-1">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: "#16A34A" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-medium" style={{ color: "#16A34A" }}>
                  {saveMessage}
                </p>
              </div>
              <button
                onClick={() => setSaveMessage("")}
                className="ml-4 flex-shrink-0"
                aria-label="Close success message"
              >
                <XMarkIcon className="w-5 h-5" style={{ color: "#16A34A" }} />
              </button>
            </div>
          )}
          {/* Error Banner */}
          {errorMessage && (
            <div
              className="mt-4 mb-4 rounded-lg border p-4 flex items-center justify-between"
              style={{
                backgroundColor: "#FEF2F2",
                borderColor: "#FCA5A5",
              }}
            >
              <div className="flex items-center gap-3 flex-1">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: "#DC2626" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-medium" style={{ color: "#DC2626" }}>
                  {errorMessage}
                </p>
              </div>
              <button
                onClick={() => setErrorMessage("")}
                className="ml-4 flex-shrink-0"
                aria-label="Close error message"
              >
                <XMarkIcon className="w-5 h-5" style={{ color: "#DC2626" }} />
              </button>
            </div>
          )}
          {/* Placeholder Info Banner */}
          <div
            className="my-4 rounded-lg border p-4"
            style={{
              backgroundColor: "#EFF6FF",
              borderColor: "#93C5FD",
            }}
          >
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: "#3B82F6" }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1" style={{ color: "#1E40AF" }}>
                  Dynamic Placeholders
                </p>
                <div className="text-sm space-y-2" style={{ color: "#1E3A8A" }}>
                  <p>
                    Text written inside double curly braces (e.g., <code className="px-1.5 py-0.5 rounded bg-blue-100" style={{ color: "#1E40AF" }}>{`{{placeholder}}`}</code>) represents dynamic values that will be automatically replaced with actual candidate information during resume generation.
                  </p>
                  <p>
                    Content wrapped inside <code className="px-1.5 py-0.5 rounded bg-blue-100" style={{ color: "#1E40AF" }}>{`<header></header>`}</code> is treated as the header in the standardized template.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            className="flex-1 mb-4"
            style={{
              borderColor: theme.palette.secondary.v4,
            }}
          >
            {isLoadingTemplate ? (
            <div className="mx-auto px-6 sm:px-12 lg:px-16 text-center mt-16 h-[calc(100dvh-72px)] w-full">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
              <p>Loading template...</p>
            </div>
          ) : (
            <TiptapEditor
              content={isEditing ? (editContent || currentContent) : currentContent}
              editable={isEditing}
              onUpdate={handleUpdate}
              showToolbar={isEditing}
            />
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePage;
